import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';

import type { KBHistoryEntry } from '@/common-types';

function formatTs(ts: string): string {
  const d = dayjs(ts);
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : ts;
}

function formatDetails(details: Record<string, unknown> | undefined): string {
  if (!details || Object.keys(details).length === 0) return '—';
  try {
    return JSON.stringify(details);
  } catch {
    return String(details);
  }
}

export interface KbHistoryPanelProps {
  entries: KBHistoryEntry[] | undefined;
  emptyMessage?: string;
}

/** Audit trail newest-first (Dynamo-backed `history` on articles / tag definitions). */
export function KbHistoryPanel({ entries, emptyMessage = 'No entries yet.' }: KbHistoryPanelProps) {
  const rows = [...(entries ?? [])].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <TableContainerSimple>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>When</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Action</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>User</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${row.timestamp}-${row.action}-${i}`} hover>
              <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatTs(row.timestamp)}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                {row.action}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', maxWidth: 200 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {row.userEmail || '—'}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                    opacity: 0.9,
                  }}
                >
                  {formatDetails(row.details)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainerSimple>
  );
}

function TableContainerSimple({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {children}
    </Box>
  );
}
